import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { authorBySlug, authorByName, type Author } from "@/content/authors";
import { blogPosts, formatPostDate } from "@/content/blog";
import { SITE_URL } from "@/lib/seo";

const poppins = { fontFamily: "'Poppins', sans-serif" } as const;
const H2 = "text-3xl font-semibold leading-tight text-ink md:text-4xl";

export const Route = createFileRoute("/author/$slug")({
  loader: ({ params }): { author: Author } => {
    const author = authorBySlug(params.slug);
    if (!author) throw notFound();
    return { author };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }] };
    }
    const { author } = loaderData;
    const url = `${SITE_URL}/author/${params.slug}/`;
    return {
      links: [{ rel: "canonical", href: url }],
      meta: [
        { title: author.metaTitle },
        { name: "description", content: author.metaDescription },
        { property: "og:title", content: author.metaTitle },
        { property: "og:description", content: author.metaDescription },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: author.name,
            jobTitle: author.title,
            url,
            ...(author.linkedin ? { sameAs: [author.linkedin] } : {}),
            worksFor: { "@type": "Organization", name: "Leadership Edge Live" },
          }),
        },
      ],
    };
  },
  notFoundComponent: AuthorMissing,
  component: AuthorPage,
});

function AuthorMissing() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <p className="text-lg text-muted-foreground">We couldn't find that author.</p>
      <Link to="/blog/" className="mt-6 inline-block font-semibold text-primary">
        Browse the blog
      </Link>
    </div>
  );
}

function AuthorPage() {
  const { author } = Route.useLoaderData();
  const posts = blogPosts.filter((p) => authorByName(p.author)?.slug === author.slug);

  return (
    <>
      <section className="bg-lavender">
        <div className="mx-auto max-w-6xl px-6 py-14 text-center md:py-20">
          <img
            src={author.photo}
            alt={author.name}
            className="mx-auto h-40 w-40 rounded-full border-4 border-background object-cover shadow-brand md:h-48 md:w-48"
          />
          <h1
            className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink md:text-5xl"
            style={poppins}
          >
            {author.name}
          </h1>
          <p className="mt-3 text-lg text-muted-foreground">{author.title}</p>
          <ul className="mt-6 flex flex-wrap justify-center gap-2">
            {author.tags.map((t) => (
              <li
                key={t}
                className="rounded-full border border-primary/20 bg-background px-3 py-1 text-xs font-semibold text-primary"
              >
                {t}
              </li>
            ))}
          </ul>
          {author.linkedin && (
            <a
              href={author.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
            >
              View LinkedIn Profile
            </a>
          )}
        </div>
      </section>

      <Section tone="default">
        <div className="mx-auto max-w-3xl">
          {author.highlights.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-4">
              {author.highlights.map((h) => (
                <div
                  key={h.value}
                  className="rounded-2xl border border-border/60 bg-background px-6 py-4"
                >
                  <p className="text-2xl font-semibold text-primary" style={poppins}>
                    {h.value}
                  </p>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {h.label}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            {author.bio.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
        </div>
      </Section>

      <Section tone="lavender">
        <SectionHeader title="Areas of Expertise" titleClassName={H2} titleStyle={poppins} />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {author.expertise.map((e) => (
            <article
              key={e.title}
              className="rounded-2xl border border-border/60 bg-background p-7 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand"
            >
              <h3 className="text-lg font-semibold leading-snug text-ink" style={poppins}>
                {e.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{e.body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section tone="default">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeader title={author.certsHeading} titleClassName={H2} titleStyle={poppins} />
            <ul className="space-y-3">
              {author.certs.map((c) => (
                <li key={c} className="flex gap-3 text-foreground">
                  <span
                    className="mt-2 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-[#693D94]"
                    aria-hidden="true"
                  />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeader
              title={author.orgsHeading}
              intro={author.orgsIntro || undefined}
              titleClassName={H2}
              titleStyle={poppins}
            />
            <ul className="flex flex-wrap gap-2">
              {author.orgs.map((o) => (
                <li
                  key={o}
                  className="rounded-full bg-lavender px-4 py-2 text-sm font-semibold text-ink"
                >
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section tone="lavender">
        <SectionHeader
          title={`Articles by ${author.name.split(" ").slice(0, 2).join(" ")}`}
          titleClassName={H2}
          titleStyle={poppins}
        />
        {posts.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to="/blog/$slug/"
                params={{ slug: p.slug }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-background transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand"
              >
                <img
                  src={p.image}
                  alt=""
                  loading="lazy"
                  className="aspect-[16/9] w-full object-cover"
                />
                <div className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {p.category}
                  </p>
                  <h3
                    className="mt-2 text-base font-semibold leading-snug text-ink"
                    style={poppins}
                  >
                    {p.title}
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground">{formatPostDate(p.date)}</p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <Link to="/blog/" className="font-semibold text-primary hover:underline">
            Browse all articles
          </Link>
        )}
        <p className="mt-10 text-sm text-muted-foreground">{author.footerLine}</p>
      </Section>

      <AdvisorCTA
        title="Not sure where to start?"
        body="Tell us what's going on with your managers. We'll point you to the right course."
      />
    </>
  );
}
